import {
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useStyle } from "./styles";
import api from "../../../../services/api";
import { notifyOnPage } from "../../../../utils/notifyOnPage";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

interface ConfigureRelatorioProps {
  relatorioData: RelatorioDataType;
  setRelatorioData: React.Dispatch<React.SetStateAction<RelatorioDataType>>;
  setHostData?: React.Dispatch<React.SetStateAction<HostDataType[]>>;
  setPerfil?: React.Dispatch<React.SetStateAction<PerfilDataType[]>>;
  setVariaveisPreview?: React.Dispatch<React.SetStateAction<Variaveis[][]>>;
  relatorioEditData?: any;
  isConfigInitialized?: boolean;
  setIsConfigInitialized?: React.Dispatch<React.SetStateAction<boolean>>;
  errors?: { title?: string; perfil?: string };
  showErrors?: boolean;
  isEditing?: boolean;
}

interface AllPerfisType {
  id: string;
  titulo: string;
  funcoes: {
    idFuncao: string;
    nomeFuncao: string;
    variaveis: Variaveis[];
    view: string;
    code: string;
    styles: string;
  }[];
  hasDefaultFunction?: string[];
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const ConfigureRelatorio: React.FC<ConfigureRelatorioProps> = ({
  relatorioData,
  setRelatorioData,
  setHostData,
  setPerfil,
  setVariaveisPreview,
  relatorioEditData,
  isConfigInitialized,
  setIsConfigInitialized,
  errors = {},
  showErrors = false,
  isEditing,
}) => {
  const theme = useTheme();
  const styles = useStyle(theme);

  const [allPerfis, setAllPerfis] = useState<AllPerfisType[]>([]);
  // console.table(relatorioData);

  useEffect(() => {
    const fetchPerfil = async () => {
      try {
        const response = await api.get("/easyReport/v2/findManyProfiles");

        setAllPerfis(response.data);
      } catch (error: any) {
        notifyOnPage(
          window.location.pathname,
          error?.response?.data?.error || "Erro ao carregar perfis",
          "error",
          3000,
        );
      }
    };
    fetchPerfil();
  }, []);

  useEffect(() => {
    if (relatorioEditData && allPerfis.length > 0 && !isConfigInitialized) {
      const profileIds =
        relatorioEditData.profiles?.map((p: any) => p.id) || [];
      const selectedPerfis = allPerfis.filter((perfil) =>
        profileIds.includes(perfil.id),
      );
      if (selectedPerfis.length > 0) {
        handlePerfisChange(selectedPerfis);
      }
      if (relatorioEditData.titulo) {
        setRelatorioData((prev) => ({
          ...prev,
          title: relatorioEditData.titulo,
        }));
      }
      if (setIsConfigInitialized) {
        setIsConfigInitialized(true);
      }
    }
  }, [relatorioEditData, allPerfis]);

  const handlePerfisChange = (newValue: AllPerfisType[]) => {
    setRelatorioData((prev) => ({
      ...prev,
      perfil: newValue.length
        ? newValue.map((p) => ({
            id: p.id,
            titulo: p.titulo,
            variaveis: p.funcoes.flatMap((f) => f.variaveis),
            view: p.funcoes.map((f) => f.view),
            code: p.funcoes.map((f) => f.code),
            styles: p.funcoes.map((f) => f.styles),
            hasDefaultFunction: p.hasDefaultFunction,
          }))
        : [],
    }));
    if (setVariaveisPreview) {
      setVariaveisPreview(
        newValue.length
          ? newValue.flatMap((p) => p.funcoes.map((f) => f.variaveis))
          : [],
      );
    }
    if (setPerfil) {
      setPerfil(
        newValue.length
          ? newValue.map((p) => ({
              id: p.id,
              titulo: p.titulo,
              variaveis: p.funcoes.flatMap((f) => f.variaveis),
              view: p.funcoes.map((f) => f.view),
              code: p.funcoes.map((f) => f.code),
              styles: p.funcoes.map((f) => f.styles),
              hasDefaultFunction: p.hasDefaultFunction,
            }))
          : [],
      );
    }
    if (setHostData) {
      if (newValue.length) {
        const profileHosts = newValue.map((p) => ({
          perfilId: p.id,
          perfilTitle: p.titulo,
          variaveis: p.funcoes.flatMap((f) =>
            f.variaveis.map((v) => ({
              id: v.id,
              titulo: v.title,
              item: v.keyName,
              hosts: [{ host: "", item: [] }],
              multiple: v.multiple,
            })),
          ),
        }));
        setHostData(profileHosts);
      } else {
        setHostData([]);
      }
    }
  };
  // useEffect(() => console.log({ relatorioData }), [relatorioData]);
  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{"Configure o Relatório"}</Typography>
        <Typography sx={styles.subtitle}>
          {"Atribua um título, adicione os hosts e seus respectivos itens"}
        </Typography>{" "}
      </Box>
      <Divider />
      <Box sx={styles.boxInput}>
        <Typography sx={styles.titleInput}>{"Adicione um Título"}</Typography>
        <TextField
          size={"small"}
          placeholder="Título do Relatório"
          value={relatorioData.title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setRelatorioData((prev) => ({
              ...prev,
              title: e.target.value,
            }));
          }}
          error={showErrors && !!errors.title}
          helperText={showErrors && errors.title ? errors.title : ""}
          disabled={isEditing}
        />
      </Box>
      <Box sx={styles.boxInput}>
        <Typography sx={styles.titleInput}>
          {"Selecione o perfil do relatório"}
        </Typography>
        <Autocomplete
          // fullWidth
          multiple
          limitTags={4}
          size="small"
          options={allPerfis}
          getOptionLabel={(option) => option.titulo}
          value={allPerfis.filter((perfil) =>
            relatorioData.perfil.some((p) => p.id === perfil.id),
          )}
          disableCloseOnSelect
          onChange={(_, newValue) => handlePerfisChange(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              error={showErrors && !!errors.perfil}
              helperText={showErrors && errors.perfil ? errors.perfil : ""}
            />
          )}
          disabled={isEditing}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <>
                <li key={key} {...optionProps}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.titulo}
                </li>
                <Divider />
              </>
            );
          }}
        />
      </Box>
      {/* <Box sx={styles.boxSwitch}>
        <Typography sx={styles.titleInput}>{"Ocultar gráficos"}</Typography>
        <Switch
          checked={relatorioData.hideGraphs}
          onChange={() => {
            setRelatorioData((prev) => ({
              ...prev,
              hideGraphs: !prev.hideGraphs,
            }));
          }}
        />
      </Box> */}
    </Box>
  );
};
